import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { login, logout } from '../../store/actions/authActions';
import { Admin_GetUserDetail_LIST } from '../../constants/adminData';
import { getSensitiveData } from '../../utils/secureStorage';

const getNameParts = (name: string) => {
  const [firstName = '', ...lastNameParts] = name.trim().split(/\s+/).filter(Boolean);
  return { firstName, lastName: lastNameParts.join(' ') };
};

export interface UserProfile {
  email: string;
  token: string;
  userId?: string;
  authToken?: string;
  planId?: number;
  adsEnabled?: boolean;
  fullCapabilities?: boolean;
  unreadNotificationsCount?: number;
  notificationRefreshVersion?: number;
  unreadMessageCount?: number;
  isProfileIncomplete: boolean;
  isProfileSummaryLoaded: boolean;
  personalInfo: {
    firstName: string;
    lastName: string;
    dateOfBirth: string;
    profileImage?: string;
    photoId?: number;
    profileImageFile?: File | null;
    gender?: string;
    weight?: number;
    heightInches?: number;
    heightFeet?: number;
    heightMeters?: number;
    heightCentimeters?: number;
    heightUnitSystem?: string;

    desiredWeight?: number;
    desiredHeightInches?: number;
    desiredHeightFeet?: number;
    desiredHeightMeters?: number;
    desiredHeightCentimeters?: number;
    desiredHeightUnitSystem?: string;
    attactiveAppearanceImportance?: string;
    desiredGenderId?: number;
    desiredExperinceMin?: number;
    desiredExperienceMax?: number;
    desiredLocation?: number;
  };

  professionalInfo: {
    itSpecialtyId?: number;
    itSpecialtyName?: string;
    currentOranizationName?: string;
    location?: {
      displayName?: string;
      latitude?: number;
      longitude?: number;
      city?: string;
      state?: string;
      country?: string;
    };
    npiNumber?: string;
    workShiftID?: number;
    workShiftDesiredID?: number;
    desiredSpeciality?: boolean | null;
    workType?: number;
    totalExperience?: number;
    organizationCount?: number;
    preferredSkills?: string;
    preferredspeciality?: string;
    preferredWorkType: number;
    preferredLocation?: string;
    availabilityType?: number;
    availableHoursPerWeek?: number;
availabilities?: {
  availabilityId?: number;
  availabilityTypeId?: number;
  availabilityTypeIds?: number[];
  availabilityTypes?: {
    availabilityTypeId: number;
    availabilityTypeName: string;
  }[];
  availabilityTypeName?: string;
  availableHoursPerWeek: number;
  defaultStartDate?: string | null;
  defaultEndDate?: string | null;
  defaultAvailableHoursPerWeek?: number;
  preferredTimeSlot?: string | null;
  availableFromDate?: string | null;
  availableToDate?: string | null;
  notes?: string | null;
  isAvailable?: boolean;
  approvalStatus?: string;
  exceptions?: {
    availabilityExceptionId: number;
    startDate: string;
    endDate: string;
    availableHoursPerWeek: number;
  }[];
}[];
  };

  backgroundInfo: {
    religionId?: number;
    desiredReligionId?: number;
    ethnicityId?: number;
    maritalStatusId?: number;
    shortBio?: string;
    desiredEthnicities?: string;
    skills?: {
      skillId: number;
      skillName: string;
      experience: number;
    }[];
    interests?:Array<object>;
  };
}

const initialState: UserProfile = {
  email: '',
  token: '',
  userId: undefined,
  authToken: '',
  planId: undefined,
  adsEnabled: false,
  fullCapabilities: false,
  unreadNotificationsCount: 0,
  notificationRefreshVersion: 0,
  unreadMessageCount: 0,
  isProfileIncomplete: false,
  isProfileSummaryLoaded: false,
  personalInfo: {
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    profileImage: '',
    gender: '',

    heightCentimeters: undefined,
    heightFeet: undefined,
    heightInches: undefined,
    heightMeters: undefined,
    heightUnitSystem: undefined,
    weight: undefined,

    desiredWeight: 0,
    desiredHeightInches: 0,
    desiredHeightFeet: 0,
    desiredHeightMeters: 0,
    desiredHeightCentimeters: 0,
    desiredHeightUnitSystem: "",
    attactiveAppearanceImportance: "",
    desiredGenderId: 0,
    desiredExperinceMin: 0,
    desiredExperienceMax: 0,
    desiredLocation: 0,
  },
  professionalInfo: {
    itSpecialtyId: 0,
    itSpecialtyName: '',
    currentOranizationName: '',
    location: {
      displayName: '',
      latitude: undefined,
      longitude: undefined,
      city: '',
      state: '',
      country: '',
    },

    npiNumber: '',
    workShiftID: 0,
    desiredSpeciality: null,
    workShiftDesiredID: 0,
    workType: 0,
    totalExperience: 0,
    organizationCount: 0,
    preferredspeciality: '',
    preferredSkills: '',
    preferredWorkType: 0,
    preferredLocation: '',
    availabilities: [],
  },
  backgroundInfo: {
    religionId: 0,
    desiredReligionId: 0,
    ethnicityId: 0,
    maritalStatusId: 0,
    shortBio: "",
    desiredEthnicities: "",
    skills: [],
    interests: [],
  },



};

export const fetchProfileSummary = createAsyncThunk(
  'userProfile/fetchSummary',
  async () => {
    const userId = await getSensitiveData('userId');
    const authToken = await getSensitiveData('authToken');

    if (!userId || !authToken) {
      throw new Error('Missing userId or authToken');
    }

    const response = await Admin_GetUserDetail_LIST(userId, authToken);
    return response.data;
  },
);

const userProfileSlice = createSlice({
  name: 'userProfile',
  initialState,
  reducers: {
    setUserProfile: (state, action: PayloadAction<UserProfile>) => {
      return action.payload;
    },

    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload;
    },

    setToken: (state, action: PayloadAction<string>) => {
      state.token = action.payload;
    },
    setUserId: (state, action: PayloadAction<string>) => {
      state.userId = action.payload;
    },
    setAuthToken: (state, action: PayloadAction<string>) => {
      state.authToken = action.payload;
    },

    setPlanId: (state, action: PayloadAction<number>) => {
      state.planId = action.payload;
    },

    setAdsEnabled: (state, action: PayloadAction<boolean>) => {
      state.adsEnabled = action.payload;
    },
    setFullCapabilities: (state, action: PayloadAction<boolean>) => {
      state.fullCapabilities = action.payload;
    },
    setUnreadNotificationsCount: (state, action: PayloadAction<number>) => {
      state.unreadNotificationsCount = action.payload;
    },
    decrementUnreadNotificationsCount: (state) => {
      state.unreadNotificationsCount = Math.max(0, (state.unreadNotificationsCount || 0) - 1);
    },
    refreshNotifications: (state) => {
      state.notificationRefreshVersion = (state.notificationRefreshVersion || 0) + 1;
    },
    setProfileIncomplete: (state, action: PayloadAction<boolean>) => {
      state.isProfileIncomplete = action.payload;
    },
    updatePersonalInfo: (
      state,
      action: PayloadAction<Partial<UserProfile['personalInfo']>>
    ) => {
      state.personalInfo = { ...state.personalInfo, ...action.payload };
    },
    updateProfessionalInfo: (
      state,
      action: PayloadAction<Partial<UserProfile['professionalInfo']>>
    ) => {
      state.professionalInfo = { ...state.professionalInfo, ...action.payload };
    },

    updateBackgroundInfo: (
      state,
      action: PayloadAction<Partial<UserProfile['backgroundInfo']>>
    ) => {
      state.backgroundInfo = { ...state.backgroundInfo, ...action.payload };
    },

    clearUserProfile: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (_state, action) => ({
        ...initialState,
        userId: action.payload.userId,
        authToken: action.payload.token,
        email: action.payload.email ?? '',
      }))
      .addCase(logout.fulfilled, () => initialState)
      .addCase(fetchProfileSummary.fulfilled, (state, action) => {
        state.isProfileSummaryLoaded = true;
        const nameParts = action.payload.name ? getNameParts(action.payload.name) : null;
        state.personalInfo = {
          ...state.personalInfo,
          firstName: nameParts?.firstName || state.personalInfo.firstName,
          lastName: nameParts?.lastName || state.personalInfo.lastName,
          profileImage: action.payload.profileImage || state.personalInfo.profileImage,
          photoId: action.payload.photoId ?? state.personalInfo.photoId,
        };
      })
      .addCase(fetchProfileSummary.rejected, (state) => {
        state.isProfileSummaryLoaded = true;
      });
  },
});

export const {
  setUserProfile,
  updatePersonalInfo,
  updateProfessionalInfo,
  updateBackgroundInfo,
  clearUserProfile,
  setEmail,
  setToken,
  setUserId,
  setAuthToken,
  setPlanId,
  setAdsEnabled,
  setFullCapabilities,
  setUnreadNotificationsCount,
  decrementUnreadNotificationsCount,
  refreshNotifications,
  setProfileIncomplete,
} = userProfileSlice.actions;

export default userProfileSlice.reducer;
